import {
  type AddedSpawn,
  applyWorldEdits,
  type EditedWorld,
  type Facing,
  type PropPlacement,
  propOrigin,
  spawnOrigin,
  type WorldEdits,
  worldEditProblems,
  type WorldLayout,
} from '@shefira/shared';

/**
 * The world editor's state (ADR 0022): the edits being made, the world they
 * give, and the way back. Pure: the editor draws what `world` says.
 *
 * An element is named by its origin, the same key `applyWorldEdits` gives:
 * `<prop>@<x>,<y>` of a generated prop, `added:<i>` of one set down here,
 * `<mob>@<x>,<y>` of a generated spawn. Moving a thing back where the
 * generator put it drops its edit, so the file only holds real changes.
 */
export class EditModel {
  private edits: WorldEdits;
  private saved: string;
  private readonly history: string[] = [];
  private cached?: { world: EditedWorld; problems: string[] };

  constructor(
    private readonly base: WorldLayout,
    edits: WorldEdits,
  ) {
    this.edits = structuredClone(edits);
    this.saved = JSON.stringify(this.edits);
  }

  get world(): EditedWorld {
    return this.evaluate().world;
  }

  /** What is wrong with the world as edited; the server refuses to save while any remain. */
  get problems(): string[] {
    return this.evaluate().problems;
  }

  get dirty(): boolean {
    return JSON.stringify(this.edits) !== this.saved;
  }

  get count(): number {
    const e = this.edits;
    return (
      e.props.length +
      e.added.length +
      e.spawns.length +
      e.addedSpawns.length +
      e.portals.length +
      e.chests.length
    );
  }

  get current(): WorldEdits {
    return structuredClone(this.edits);
  }

  markSaved(): void {
    this.saved = JSON.stringify(this.edits);
  }

  /** Remember the state before a gesture (a drag is one step back, however far it went). */
  checkpoint(): void {
    const state = JSON.stringify(this.edits);
    if (this.history.at(-1) !== state) this.history.push(state);
    if (this.history.length > 200) this.history.shift();
  }

  undo(): boolean {
    const previous = this.history.pop();
    if (previous === undefined) return false;
    this.edits = JSON.parse(previous) as WorldEdits;
    this.cached = undefined;
    return true;
  }

  placeProp(origin: string, to: { x: number; y: number; flip: boolean }): void {
    const added = addedIndex(origin);
    if (added !== undefined) {
      const placed = this.edits.added[added];
      if (placed !== undefined) this.edits.added[added] = { ...placed, ...to };
    } else {
      const generated = this.base.props.find((prop) => propOrigin(prop) === origin);
      if (generated === undefined) return;
      const rest = this.edits.props.filter((edit) => propOrigin(edit) !== origin);
      const unchanged = generated.x === to.x && generated.y === to.y && generated.flip === to.flip;
      this.edits.props = unchanged
        ? rest
        : [...rest, { prop: generated.prop, x: generated.x, y: generated.y, to }];
    }
    this.changed();
  }

  removeProp(origin: string): void {
    const added = addedIndex(origin);
    if (added !== undefined) {
      this.edits.added.splice(added, 1);
    } else {
      const generated = this.base.props.find((prop) => propOrigin(prop) === origin);
      if (generated === undefined) return;
      this.edits.props = [
        ...this.edits.props.filter((edit) => propOrigin(edit) !== origin),
        { prop: generated.prop, x: generated.x, y: generated.y, to: null },
      ];
    }
    this.changed();
  }

  addProp(placed: PropPlacement): void {
    this.edits.added.push(placed);
    this.changed();
  }

  addSpawn(spawn: AddedSpawn): void {
    this.edits.addedSpawns.push(spawn);
    this.changed();
  }

  /** Move, turn, or resize a spawn: what `to` leaves out stays as it is. */
  placeSpawn(origin: string, to: SpawnPlacement): void {
    const added = indexOf(origin, 'added-spawn:');
    if (added !== undefined) {
      const spawn = this.edits.addedSpawns[added];
      if (spawn !== undefined) this.edits.addedSpawns[added] = { ...spawn, ...to };
      this.changed();
      return;
    }
    const generated = this.base.dens.find((den) => spawnOrigin(den) === origin);
    if (generated === undefined) return;
    const previous = this.edits.spawns.find((edit) => spawnOrigin(edit) === origin)?.to ?? {};
    const next = { ...previous, ...to };
    const rest = this.edits.spawns.filter((edit) => spawnOrigin(edit) !== origin);
    const unchanged =
      generated.x === next.x &&
      generated.y === next.y &&
      (next.facing ?? generated.facing) === generated.facing &&
      (next.count ?? generated.count) === generated.count &&
      (next.radius ?? generated.radius) === generated.radius;
    this.edits.spawns = unchanged
      ? rest
      : [...rest, { mob: generated.mob, x: generated.x, y: generated.y, to: next }];
    this.changed();
  }

  removeSpawn(origin: string): void {
    const added = indexOf(origin, 'added-spawn:');
    if (added !== undefined) {
      this.edits.addedSpawns.splice(added, 1);
      this.changed();
      return;
    }
    const generated = this.base.dens.find((den) => spawnOrigin(den) === origin);
    if (generated === undefined) return;
    this.edits.spawns = [
      ...this.edits.spawns.filter((edit) => spawnOrigin(edit) !== origin),
      { mob: generated.mob, x: generated.x, y: generated.y, to: null },
    ];
    this.changed();
  }

  placePortal(id: string, to: { x: number; y: number }): void {
    const generated = this.base.portals.find((portal) => portal.id === id);
    if (generated === undefined) return;
    const rest = this.edits.portals.filter((edit) => edit.id !== id);
    this.edits.portals =
      generated.x === to.x && generated.y === to.y ? rest : [...rest, { id, ...to }];
    this.changed();
  }

  placeChest(id: string, to: { x: number; y: number }): void {
    const generated = this.base.chests.find((chest) => chest.id === id);
    if (generated === undefined) return;
    const rest = this.edits.chests.filter((edit) => edit.id !== id);
    this.edits.chests =
      generated.x === to.x && generated.y === to.y ? rest : [...rest, { id, ...to }];
    this.changed();
  }

  private changed(): void {
    this.cached = undefined;
  }

  private evaluate(): { world: EditedWorld; problems: string[] } {
    if (this.cached === undefined) {
      const world = applyWorldEdits(this.base, this.edits);
      this.cached = { world, problems: worldEditProblems(this.base, world) };
    }
    return this.cached;
  }
}

export interface SpawnPlacement {
  x: number;
  y: number;
  facing?: Facing;
  count?: number;
  radius?: number;
}

function addedIndex(origin: string): number | undefined {
  return indexOf(origin, 'added:');
}

function indexOf(origin: string, prefix: string): number | undefined {
  if (!origin.startsWith(prefix)) return undefined;
  const index = Number(origin.slice(prefix.length));
  return Number.isInteger(index) ? index : undefined;
}
